import React, { useState } from 'react';
import ContactModal from '../components/ContactModal';
import './Programs.css';

const Programs = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <div className="page-container">
            {/* Hero Section */}
            <section className="page-hero-section programs-hero">
                <div className="page-hero-content">
                    <p className="page-hero-subtitle">OUR PROGRAMS</p>
                    <h1 className="page-hero-title">
                        Growing Kenya's Chess<br />
                        Community Together
                    </h1>
                    <p className="page-hero-description">
                        From schools to corporates, from beginners to champions—we offer tailored chess programs that meet you where you are. Join us in building a thriving chess community across Kenya, one move at a time.
                    </p>
                    <div className="hero-cta">
                        <button
                            className="btn-cta-primary"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Join a Program Today
                        </button>
                    </div>
                </div>
            </section>

            {/* Chess for Schools Program */}
            <section className="program-section schools-program">
                <div className="container">
                    <h2>Chess for Schools</h2>
                    <p className="section-intro">
                        Transform your school into a hub of strategic thinking and academic excellence. Our comprehensive school programs integrate chess into your institution, benefiting students, enhancing your reputation, and creating lasting impact.
                    </p>

                    <div className="program-cards-grid">
                        <div className="program-card">
                            <div className="program-icon">🧠</div>
                            <h3>Benefits for Students</h3>
                            <p>
                                Chess develops critical thinking, improves concentration, enhances memory, and builds emotional resilience in students. Beyond academics, it teaches patience, strategic planning, and graceful handling of both victory and defeat—life skills that extend far beyond the chessboard.
                            </p>
                        </div>

                        <div className="program-card">
                            <div className="program-icon">🏫</div>
                            <h3>Benefits for Schools</h3>
                            <p>
                                Elevate your school's reputation through tournament wins and media coverage. Attract quality-conscious parents seeking holistic education. Gain local and international exposure as your students compete in tournaments across Kenya and globally, putting your institution on the map.
                            </p>
                        </div>

                        <div className="program-card">
                            <div className="program-icon">📚</div>
                            <h3>Integration Options</h3>
                            <p>
                                Choose between curriculum-based lessons with structured assessments and certificates, or establish an extracurricular club for competitive training. Both options are flexible, professionally managed, and designed to fit seamlessly into your school's schedule and goals.
                            </p>
                        </div>
                    </div>

                    <div className="section-cta">
                        <button
                            className="btn-cta-primary"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Start a School Program
                        </button>
                    </div>
                </div>
            </section>

            {/* Private Chess Classes Program */}
            <section className="program-section private-program">
                <div className="container">
                    <h2>Private Chess Classes</h2>
                    <p className="section-intro">
                        Personalized coaching tailored to your unique learning style and goals. Whether you're a complete beginner or an aspiring champion, our expert coaches provide one-on-one guidance to help you thrive in your chess journey.
                    </p>

                    <div className="program-cards-grid">
                        <div className="program-card">
                            <div className="program-icon">👥</div>
                            <h3>Build Community Connections</h3>
                            <p>
                                Join a vibrant community of chess enthusiasts. Through our private classes, you'll connect with fellow players, participate in club events, and build lasting friendships around this beautiful game. Chess brings people together—you're never alone on this journey.
                            </p>
                        </div>

                        <div className="program-card">
                            <div className="program-icon">🏆</div>
                            <h3>Challenge Yourself Competitively</h3>
                            <p>
                                Push your limits and test your skills in FIDE-rated tournaments. Our coaching prepares you for competitive play at local, national, and international levels. Some of our students have gone on to represent Kenya globally—your chess dreams are within reach.
                            </p>
                        </div>

                        <div className="program-card">
                            <div className="program-icon">🎯</div>
                            <h3>Cultivate a Hobby for the Mind</h3>
                            <p>
                                Chess is more than a game—it's a lifelong pursuit that keeps your mind sharp, engaged, and constantly learning. Develop a hobby that challenges you intellectually, provides endless depth, and offers satisfaction with every improvement you make.
                            </p>
                        </div>
                    </div>

                    <div className="section-cta">
                        <button
                            className="btn-cta-primary"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Book Private Coaching
                        </button>
                    </div>
                </div>
            </section>

            {/* Corporate Chess Program */}
            <section className="program-section corporate-program">
                <div className="container">
                    <h2>Corporate Chess</h2>
                    <p className="section-intro">
                        Looking for a unique team-building activity that actually works? Chess brings your team together in a way that's engaging, inclusive, and genuinely beneficial. It's not just another corporate event—it's an investment in your team's cohesion and morale.
                    </p>

                    <div className="program-cards-grid">
                        <div className="program-card">
                            <div className="program-icon">🤝</div>
                            <h3>Build Team Chemistry</h3>
                            <p>
                                Tired of generic team-building exercises that feel forced? Chess creates natural opportunities for collaboration, friendly competition, and mutual respect. Employees bond over shared challenges, learn from each other, and develop genuine camaraderie that translates to better workplace relationships.
                            </p>
                        </div>

                        <div className="program-card">
                            <div className="program-icon">⚔️</div>
                            <h3>Inter-Corporate Tournaments</h3>
                            <p>
                                Represent your company in inter-corporate chess tournaments and networking events. It's a professional, engaging way to connect with other businesses while showcasing your company culture. Plus, tournament wins mean positive PR and employee pride in representing your brand.
                            </p>
                        </div>

                        <div className="program-card">
                            <div className="program-icon">😊</div>
                            <h3>Boost Employee Morale</h3>
                            <p>
                                Struggling with employee engagement and workplace stress? Chess provides a mental break that's actually productive. It reduces burnout, offers a healthy competitive outlet, and shows employees you value their well-being beyond just work output. Happy employees are productive employees.
                            </p>
                        </div>
                    </div>

                    <div className="section-cta">
                        <button
                            className="btn-cta-primary"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Start Corporate Program
                        </button>
                    </div>
                </div>
            </section>

            {/* Chess for Therapy Program */}
            <section className="program-section therapy-program">
                <div className="container">
                    <h2>Chess for Therapy</h2>
                    <p className="section-intro">
                        Chess has proven to be a powerful therapeutic tool for mental health recovery and emotional well-being. From addiction recovery to managing depression and anxiety, chess provides structure, focus, and hope for individuals working through life's challenges.
                    </p>

                    <div className="program-cards-grid">
                        <div className="program-card">
                            <div className="program-icon">🧘</div>
                            <h3>Structured Mental Engagement</h3>
                            <p>
                                For therapists working with clients struggling with addiction or mental health issues, chess offers structured cognitive engagement that's non-threatening and rewarding. It provides clear goals, immediate feedback, and a sense of accomplishment—crucial elements in recovery and mental health treatment.
                            </p>
                        </div>

                        <div className="program-card">
                            <div className="program-icon">💪</div>
                            <h3>Building Resilience & Coping Skills</h3>
                            <p>
                                Chess teaches clients to handle setbacks, think ahead, and make decisions under pressure—all essential coping mechanisms. For those in recovery or managing mental health conditions, these skills translate directly to real-life challenges, building confidence and emotional resilience.
                            </p>
                        </div>

                        <div className="program-card">
                            <div className="program-icon">🌟</div>
                            <h3>Social Connection & Purpose</h3>
                            <p>
                                Isolation is a common challenge in therapy settings. Chess creates opportunities for social interaction in a safe, structured environment. Clients find community, develop friendships, and discover a sense of purpose—all critical factors in successful therapeutic outcomes and long-term well-being.
                            </p>
                        </div>
                    </div>

                    <div className="section-cta">
                        <button
                            className="btn-cta-primary"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Learn About Therapeutic Chess
                        </button>
                    </div>
                </div>
            </section>

            <ContactModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </div>
    );
};

export default Programs;
